import React from "react";
import { useFormik } from "formik";
import { BaseTableColumn, FieldData } from "../tables/BaseTable";
import {
  Box,
  HStack,
  Field,
  Select,
  Portal,
  createListCollection,
  Combobox,
  useFilter,
  useListCollection,
} from "@chakra-ui/react";
import {
  IEmailAIExtractedData,
  IEmailAIExtractedOwner,
} from "../../types/common";
import { formatDateString } from "@/utils/date";
import FilterAutocomplete from "./FilterAutocomplete";
import { useQuery } from "@apollo/client";
import { DATA_FOR_FILTERS_IN_REVIEW_AND_PROCESSED_EMAILS } from "@/api/queries";

export type FiltersFormData = {
  [key in BaseTableColumn]?: string[];
};

export interface ISearchFormData extends FiltersFormData {
  ssn?: string;
}

interface TableFiltersFormProps {
  setFilters: (filters: Record<string, any>) => void;
  renderFilters: TableSelector[];
  dataForFilters: Record<string, string[]>;
  dataForFiltersLoading: boolean;

  tableInitialLoading?: boolean;
}

export interface TableFilter {
  label: string;
}

const TableFiltersForm = ({
  setFilters,
  renderFilters,
  dataForFilters,
  dataForFiltersLoading,
  tableInitialLoading = false,
}: TableFiltersFormProps) => {

  const {
    handleSubmit,
    values,
    handleChange,
    setFieldValue,
    errors,
    touched,
    submitForm,
  } = useFormik<Record<string, any>>({
    initialValues: {},
    onSubmit: async (formData) => {
      const transformed = Object.fromEntries(
        Object.entries(formData).map(([key, value]) => 
          [TABLE_FILTERS[key as TableSelector].queryName, value[0]]
        )
      );
      
      setFilters(transformed);
    },
  });

  return (
    <Box width="100%" p={8}>
      <form onSubmit={handleSubmit}>
        <HStack gap={8}>
          {renderFilters.map((selector: TableSelector, i: number) => {
            const { label } = TABLE_FILTERS[
              selector
            ] as TableFilter;

            const customOptions = (
              TABLE_FILTERS[selector] as any
            )?.getInitialItems?.(dataForFilters?.[selector]);

            let options: { label: string; value: string; }[] = mapOptions(dataForFilters?.[selector])

            if (!dataForFilters || dataForFiltersLoading)
              return (
                <Field.Root invalid={!!errors[selector]}>
                  <Field.Label>{label}</Field.Label>
                  <Combobox.Root disabled>
                    <Combobox.Control>
                      <Combobox.Input placeholder="Type to search" />
                      <Combobox.IndicatorGroup>
                        <Combobox.ClearTrigger />
                        <Combobox.Trigger />
                      </Combobox.IndicatorGroup>
                    </Combobox.Control>

                    <Portal>
                      <Combobox.Positioner>
                        <Combobox.Content maxH="min(280px, 50vh)" overflowY="auto">
                          <Combobox.Empty>No items found</Combobox.Empty>
                        </Combobox.Content>
                      </Combobox.Positioner>
                    </Portal>
                  </Combobox.Root>
                </Field.Root>
              );
            return (
              <FilterAutocomplete
                key={i}
                items={customOptions ?? options}
                selector={selector}
                setFieldValue={setFieldValue}
                submitForm={submitForm}
                errors={errors}
                values={values}
                disabled={tableInitialLoading}
              />
            );
          })}
        </HStack>
      </form>
    </Box>
  );
};

export const TABLE_FILTERS = {
  businessNames: {
    label: "Business Name",
    queryName: "businessName"
  },
  dbas: {
    label: "Business DBA",
    queryName: "dba"
  },
  owners: {
    label: "Owners",
    queryName: "owner"
  },
  processingDates: {
    label: "Processing date",
    queryName: "processingDate",
    getInitialItems: (data: string[]) =>
      data?.map((date) => ({
        label: formatDateString(date),
        value: date,
      })),
  },
  ssns: {
    label: "SSN",
    queryName: "ssn",
  },
};

export type TableSelector = keyof typeof TABLE_FILTERS;

const mapOptions = (options: string[]) => options
  ?.map(o => ({ label: o, value: o}))

export default TableFiltersForm;
